import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Award } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function OBEReports() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/obe/stats`, { withCredentials: true }).then(r => setStats(r.data)).catch(() => {});
  }, []);

  const programs = stats?.programs || [];
  const chartData = programs.map(p => ({ name: p.name.replace("B.Tech ", "").replace(" / ", "/"), cos: p.attainment_cos, pos: p.attainment_pos }));

  return (
    <div className="space-y-6" data-testid="obe-module">
      <div className="border-b border-[#D8D4CB] pb-5">
        <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#BDBAA8] mb-1">Quality & Compliance</p>
        <h2 className="font-['Syne'] font-bold text-2xl text-[#0C0C10]">OBE / NAAC Reports</h2>
      </div>

      {/* NAAC Score Banner */}
      <div className="bg-[#0C0C10] p-6 flex items-center justify-between">
        <div>
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#EAFF00] mb-2">NAAC Accreditation</p>
          <p className="font-['Instrument_Serif'] text-5xl text-[#EEEADF] leading-none">{stats?.naac_score || "3.24"}</p>
          <p className="font-['Syne'] font-bold text-sm text-[#EEEADF]/60 mt-2">Grade {stats?.accreditation || "A+"} — Valid till {stats?.next_cycle || "2027"}</p>
        </div>
        <div className="text-right">
          <div className="w-20 h-20 border-4 border-[#EAFF00] flex items-center justify-center">
            <span className="font-['Instrument_Serif'] text-3xl italic text-[#EAFF00]">{stats?.accreditation || "A+"}</span>
          </div>
          <p className="font-['DM_Sans'] text-xs text-[#EEEADF]/30 mt-2">Last visit: {stats?.last_visit || "2022-11-15"}</p>
        </div>
      </div>

      {/* Program Attainment */}
      <div className="bg-white border border-[#E2DDD4] p-6">
        <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-5">Program Attainment — CO vs PO</p>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="2 4" stroke="#F0ECE4" />
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#BDBAA8", fontFamily: "Syne" }} axisLine={false} tickLine={false} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#BDBAA8", fontFamily: "Syne" }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: "#0C0C10", border: "1px solid #252530", borderRadius: 0, fontSize: 12 }} />
            <Bar dataKey="cos" name="CO Attainment" fill="#0A7AFF" radius={[2, 2, 0, 0]} />
            <Bar dataKey="pos" name="PO Attainment" fill="#EAFF00" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Program Table */}
      <div className="bg-white border border-[#E2DDD4]">
        <div className="grid gap-0 px-5 py-3 border-b border-[#E2DDD4] bg-[#FAFAF7]" style={{ gridTemplateColumns: "2fr 100px 100px 80px" }}>
          {["Program", "CO Attainment", "PO Attainment", "Gap"].map(h => (
            <span key={h} className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8]">{h}</span>
          ))}
        </div>
        {programs.map((p, i) => (
          <div key={i} className="grid gap-0 px-5 py-3.5 border-b border-[#F0ECE4] last:border-0 items-center" style={{ gridTemplateColumns: "2fr 100px 100px 80px" }}>
            <p className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{p.name}</p>
            <div>
              <span className="font-['JetBrains_Mono'] text-xs font-bold text-[#0A7AFF]">{p.attainment_cos}%</span>
              <div className="w-16 h-1 bg-[#F0ECE4] mt-1"><div className="h-1 bg-[#0A7AFF]" style={{ width: `${p.attainment_cos}%` }} /></div>
            </div>
            <div>
              <span className="font-['JetBrains_Mono'] text-xs font-bold text-[#EAFF00] [text-shadow:0_0_0_#0C0C10] filter" style={{ color: "#c8b800" }}>{p.attainment_pos}%</span>
              <div className="w-16 h-1 bg-[#F0ECE4] mt-1"><div className="h-1 bg-[#EAFF00]" style={{ width: `${p.attainment_pos}%` }} /></div>
            </div>
            <span className={`font-['JetBrains_Mono'] text-xs font-bold ${p.gap > 5 ? "text-[#FF3636]" : "text-[#22C55E]"}`}>{p.gap}%</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { label: "IQAC Reports", sub: "AQAR, IIQA, SSR", action: "Download Reports" },
          { label: "Bloom's Taxonomy", sub: "CO mapping analysis", action: "View Analysis" },
          { label: "Gap Analysis", sub: "Identify weak areas", action: "Run Analysis" },
        ].map((c, i) => (
          <div key={i} className="bg-white border border-[#E2DDD4] p-5">
            <div className="w-8 h-8 bg-[#0C0C10] flex items-center justify-center mb-3"><Award className="w-4 h-4 text-[#EAFF00]" /></div>
            <p className="font-['Syne'] font-bold text-sm text-[#0C0C10] mb-1">{c.label}</p>
            <p className="font-['DM_Sans'] text-xs text-[#7A7468] mb-4">{c.sub}</p>
            <button className="font-['Syne'] font-bold text-[9px] uppercase tracking-wider border border-[#D8D4CB] text-[#0C0C10] px-4 py-2 hover:bg-[#0C0C10] hover:text-[#EAFF00] hover:border-[#0C0C10] transition-all">{c.action}</button>
          </div>
        ))}
      </div>
    </div>
  );
}
